import { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const nf = new Intl.NumberFormat('it-IT');
const METRICS = {
  tokens: { label: 'Token', key: 'total_tokens', fmt: (v) => nf.format(v) },
  cost: { label: 'Costo $', key: 'cost_usd', fmt: (v) => `$${Number(v).toFixed(4)}` },
};

function buildSeries(byDay, days) {
  const map = Object.fromEntries(byDay.map((d) => [d.key, d]));
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    const row = map[key];
    out.push({ key, label: `${key.slice(8, 10)}/${key.slice(5, 7)}`, total_tokens: row?.total_tokens || 0, cost_usd: row?.cost_usd || 0, calls: row?.calls || 0 });
  }
  return out;
}

/** Grafico a barre giornaliero di token o costo stimato. */
export function UsageDailyChart({ byDay, days }) {
  const [metric, setMetric] = useState('tokens');
  const series = useMemo(() => buildSeries(byDay, days), [byDay, days]);
  const m = METRICS[metric];
  const btn = (active) => `px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors ${active ? 'bg-ink text-paper border-ink' : 'border-line text-soft hover:text-ink'}`;

  return (
    <section className="border border-line bg-paper" data-testid="llm-daily-chart">
      <div className="px-4 py-3 border-b border-linesoft flex items-center justify-between">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-soft">Andamento giornaliero</h2>
        <div className="flex">
          {Object.entries(METRICS).map(([k, v]) => (
            <button key={k} type="button" className={btn(metric === k)} onClick={() => setMetric(k)} data-testid={`llm-chart-metric-${k}`}>{v.label}</button>
          ))}
        </div>
      </div>
      <div className="h-64 px-2 py-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--qp-border-soft)" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--qp-text-mute)' }} tickLine={false} axisLine={{ stroke: 'var(--qp-border)' }} interval="preserveStartEnd" minTickGap={16} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--qp-text-mute)' }} tickLine={false} axisLine={false} width={64} tickFormatter={m.fmt} />
            <Tooltip
              cursor={{ fill: 'var(--qp-bg-hover)' }}
              contentStyle={{ background: 'var(--qp-card)', border: '1px solid var(--qp-border)', borderRadius: 0, fontSize: 12 }}
              labelFormatter={(_, p) => p?.[0]?.payload?.key || ''}
              formatter={(v, _n, p) => [`${m.fmt(v)} · ${p.payload.calls} chiamate`, m.label]}
            />
            <Bar dataKey={m.key} fill="var(--qp-ink)" radius={[2, 2, 0, 0]} maxBarSize={40} isAnimationActive />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
